import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { Store } from '@ngrx/store';
import { merge, mergeWith, Subject } from 'rxjs';
import { debounceTime, distinctUntilKeyChanged, filter, takeUntil } from 'rxjs/operators';
import { RxEmitter } from 'common/rxjs/rx-emitter';
import { WorkItemStateHandler } from 'shared/services/workitem-state-handler/workitem-state-handler.service';
import { MessageBusEvents } from 'common/common/constants/message-bus-events';
import { ResizeObserverService } from 'shared/services/resize-observer/resize-observer.service';
import { selectLastExtendedOutboundMessageByConversationId } from 'common/store/genesys-cloud/conversations/messages/selectors/message.selectors';
import { Contact } from 'common/store/genesys-cloud/external-contacts/models/contact.model';

@Component({
  selector: 'app-genesys-chat-box-scrollable-body',
  templateUrl: './genesys-chat-box-scrollable-body.component.html',
  styleUrls: ['./genesys-chat-box-scrollable-body.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GenesysChatBoxScrollableBodyComponent implements OnDestroy, OnChanges, AfterViewInit {
  static readonly UpdateIsAutoScrollEnabledDelayMillis = 500;
  static readonly ScrollToBottomDelayMillis = 100;
  static readonly BottomScrollTolerancePixels = 10;
  private readonly onDestroySubject = new Subject<void>();
  private readonly onScrollSubject = new Subject<void>();
  private readonly onContentSizeChangeSubject = new Subject<void>();
  private _isAutoScrollEnabled = true;

  @Input()
  contact: Contact;

  @Input()
  conversationId: string;

  @Input()
  isMessagingPeriodEnded: boolean;

  @ViewChild('messageList', { read: ElementRef })
  messageList: ElementRef<HTMLElement>;

  constructor(
    private readonly el: ElementRef<HTMLElement>,
    private readonly rxEmitter: RxEmitter,
    private readonly workItemStateHandler: WorkItemStateHandler,
    private readonly resizeObserverService: ResizeObserverService,
    private readonly store: Store
  ) {}

  get isAutoScrollEnabled(): boolean {
    return this._isAutoScrollEnabled;
  }

  ngOnChanges({ isMessagingPeriodEnded }: SimpleChanges): void {
    if (isMessagingPeriodEnded?.currentValue) {
      this.onContentSizeChangeSubject.next();
    }
  }

  ngOnDestroy(): void {
    this.onDestroySubject.next();
    this.onDestroySubject.complete();
  }

  ngAfterViewInit(): void {
    this.createUpdateIsAutoScrollEnabledSubscription();
    this.createScrollToBottomSubscription();
  }

  @HostListener('scroll')
  onScrollMessageList(): void {
    this.onScrollSubject.next();
  }

  scrollToBottom(): void {
    requestAnimationFrame(() => {
      this.el.nativeElement.scrollTop = this.el.nativeElement.scrollHeight;
    });
  }

  updateIsAutoScrollEnabled(): void {
    this._isAutoScrollEnabled =
      this.el.nativeElement.scrollTop + this.el.nativeElement.clientHeight >=
      this.el.nativeElement.scrollHeight - GenesysChatBoxScrollableBodyComponent.BottomScrollTolerancePixels;
  }

  onImageLoad(): void {
    this.onContentSizeChangeSubject.next();
  }

  private createUpdateIsAutoScrollEnabledSubscription(): void {
    this.onScrollSubject
      .pipe(
        debounceTime(GenesysChatBoxScrollableBodyComponent.UpdateIsAutoScrollEnabledDelayMillis),
        takeUntil(this.onDestroySubject)
      )
      .subscribe(() => {
        this.updateIsAutoScrollEnabled();
      });
  }

  private createScrollToBottomSubscription(): void {
    const onExpandedModeChanged$ = this.rxEmitter.getNamespaceObservable(MessageBusEvents.ToggleExpandedMode);
    const onWorkItemFocusChanged$ = this.workItemStateHandler.workItemFocused$;
    const onResize$ = this.resizeObserverService.observeResize$(this.el);
    const onResizeList$ = this.resizeObserverService.observeResize$(this.messageList);

    // We need to use extended messages to avoid timing issues, because this is exactly what gets displayed in the messages list
    const onOutboundMessage$ = this.store
      .select(selectLastExtendedOutboundMessageByConversationId(this.conversationId))
      .pipe(filter(Boolean), distinctUntilKeyChanged('id'));

    merge(onExpandedModeChanged$, onWorkItemFocusChanged$, onResize$, onResizeList$, this.onContentSizeChangeSubject)
      .pipe(
        filter(() => this.isAutoScrollEnabled),
        mergeWith(onOutboundMessage$), // after filter, so that every new outbound message will trigger a scroll
        debounceTime(GenesysChatBoxScrollableBodyComponent.ScrollToBottomDelayMillis),
        takeUntil(this.onDestroySubject)
      )
      .subscribe(() => this.scrollToBottom());
  }
}
